import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Departments({ user, onLogout }) {
  const [departments, setDepartments] = useState([]);
  const [tree, setTree] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', department_head_id: '', parent_department_id: ''
  });

  useEffect(() => {
    fetchDepartments();
    fetchDashboardStats();
  }, []);

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      const [deptRes, treeRes] = await Promise.all([
        axios.get(`${API}/departments`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/departments/tree`, { headers: { Authorization: `Bearer ${token}` }})
      ]);
      setDepartments(deptRes.data);
      setTree(treeRes.data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/departments`, {
        ...formData,
        id: Date.now().toString()
      }, { headers: { Authorization: `Bearer ${token}` }});
      setShowForm(false);
      setFormData({ name: '', description: '', department_head_id: '', parent_department_id: '' });
      fetchDepartments();
    } catch (error) {
      alert('Failed to create department');
    }
  };

  const renderTree = (depts, level = 0) => {
    return depts.map(dept => (
      <div key={dept.id} style={{ marginLeft: `${level * 30}px` }} className="mb-2">
        <div className="flex items-center p-3 bg-white rounded-lg shadow">
          <span className="text-2xl mr-3">🏢</span>
          <div>
            <p className="font-bold">{dept.name}</p>
            {dept.description && <p className="text-sm text-gray-600">{dept.description}</p>}
          </div>
        </div>
        {dept.children && dept.children.length > 0 && renderTree(dept.children, level + 1)}
      </div>
    ));
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Departments</h1>
          <div className="space-x-4">
            <button onClick={() => setViewMode('list')} className={`px-4 py-2 rounded-lg ${viewMode === 'list' ? 'bg-[#2b338015]0 text-white' : 'bg-gray-200'}`}>List View</button>
            <button onClick={() => setViewMode('tree')} className={`px-4 py-2 rounded-lg ${viewMode === 'tree' ? 'bg-[#2b338015]0 text-white' : 'bg-gray-200'}`}>Org Tree</button>
            <button onClick={() => setShowForm(!showForm)} className="px-6 py-2 rounded-lg text-white" style={{backgroundColor: '#2b3380'}}>+ Add Department</button>
          </div>
        </div>

        {/* Department Module Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="Department Overview"
            icon="🏢"
            bgColor="#7e57c2"
            stats={[
              {
                label: 'Total Departments',
                value: dashboardStats.total_departments || 0,
                subtitle: 'Active departments',
                color: '#7e57c2'
              },
              {
                label: 'Total Employees',
                value: dashboardStats.total_employees || 0,
                subtitle: 'Across all depts',
                color: '#2b3380'
              },
              {
                label: 'Pending Reviews',
                value: dashboardStats.pending_reviews || 0,
                subtitle: 'Dept reviews',
                color: '#e91e63'
              },
              {
                label: 'Active Shifts',
                value: dashboardStats.total_shifts || 0,
                subtitle: 'Configured',
                color: '#607d8b'
              }
            ]}
          />
        )}

        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Add Department</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
              <input type="text" placeholder="Department Name *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="px-3 py-2 border rounded-lg" />
              <input type="text" placeholder="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="px-3 py-2 border rounded-lg" />
              <select value={formData.parent_department_id} onChange={(e) => setFormData({...formData, parent_department_id: e.target.value})} className="px-3 py-2 border rounded-lg">
                <option value="">No Parent (Root Department)</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <div className="flex gap-2">
                <button type="submit" className="px-6 py-2 rounded-lg text-white" style={{backgroundColor: '#2b3380'}}>Create</button>
                <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2 rounded-lg border">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {viewMode === 'list' ? (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="w-full">
              <thead style={{backgroundColor: '#f5f7fa'}}>
                <tr>
                  <th className="px-6 py-3 text-left">Department Name</th>
                  <th className="px-6 py-3 text-left">Description</th>
                  <th className="px-6 py-3 text-left">Parent</th>
                  <th className="px-6 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {departments.map(dept => (
                  <tr key={dept.id} className="border-t">
                    <td className="px-6 py-4 font-medium">{dept.name}</td>
                    <td className="px-6 py-4 text-gray-600">{dept.description || 'N/A'}</td>
                    <td className="px-6 py-4 text-gray-600">{dept.parent_department_id ? departments.find(d => d.id === dept.parent_department_id)?.name : 'Root'}</td>
                    <td className="px-6 py-4"><button className="text-[#2b3380] hover:underline">Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4">Organization Tree</h2>
            {renderTree(tree)}
          </div>
        )}
      </div>
    </Layout>
  );
}
export default Departments;
